import { useState } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { signLease } from "../lib/api";
import { FileText, CheckCircle2, Clock, PenSquare, Calendar, DollarSign, Shield } from "lucide-react";

export default function LeaseCard({ lease, currentUserId, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState("");

  const isRenter = currentUserId === lease.renter_id;
  const isLandlord = currentUserId === lease.landlord_id;
  const mySigned = isRenter ? lease.renter_signed : isLandlord ? lease.landlord_signed : false;
  const otherSigned = isRenter ? lease.landlord_signed : isLandlord ? lease.renter_signed : false;
  const otherLabel = isRenter ? "Landlord" : "Renter";

  const statusColor =
    lease.status === "active" ? "bg-[#1a6b3c]/10 text-[#1a6b3c]" :
    lease.status === "pending_signatures" ? "bg-amber-100 text-amber-700" :
    "bg-[#e5e3de] text-[#5a5a5a]";

  const statusLabel =
    lease.status === "active" ? "Active" :
    lease.status === "pending_signatures" ? "Awaiting signatures" :
    lease.status;

  const handleSign = async () => {
    setSigning(true);
    setError("");
    try {
      const res = await signLease(lease.lease_id);
      if (onUpdate) onUpdate(res.data);
      setOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to sign. Please try again.");
    }
    setSigning(false);
  };

  return (
    <>
      <div
        className="bg-white rounded-xl p-5 border border-[#d5d3ce]/60 hover:border-[#1a6b3c]/40 hover:shadow-md transition-all cursor-pointer"
        onClick={() => setOpen(true)}
        data-testid={`lease-card-${lease.lease_id}`}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#1a6b3c]/10 flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-[#1a6b3c]" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-[#0d0d0d] font-['Fraunces'] truncate">
                Lease #{lease.lease_id.slice(-6).toUpperCase()}
              </p>
              <p className="text-xs text-[#888] capitalize">
                {lease.lease_type} · ${lease.monthly_rent?.toLocaleString()}/mo
              </p>
            </div>
          </div>
          <Badge className={`rounded-full text-xs font-semibold ${statusColor} border-0 shrink-0`}>
            {statusLabel}
          </Badge>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
          <div className="flex items-center gap-1.5 text-[#5a5a5a]">
            <div className={`w-2 h-2 rounded-full ${mySigned ? "bg-[#1a6b3c]" : "bg-amber-400"}`} />
            You: {mySigned ? "Signed" : "Pending"}
          </div>
          <div className="flex items-center gap-1.5 text-[#5a5a5a]">
            <div className={`w-2 h-2 rounded-full ${otherSigned ? "bg-[#1a6b3c]" : "bg-amber-400"}`} />
            {otherLabel}: {otherSigned ? "Signed" : "Pending"}
          </div>
        </div>

        {!mySigned && lease.status !== "active" ? (
          <Button
            data-testid={`lease-sign-btn-${lease.lease_id}`}
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            size="sm"
            className="w-full rounded-xl bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] font-semibold text-sm"
          >
            <PenSquare className="w-3.5 h-3.5 mr-1.5" /> Review & Sign
          </Button>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            className="w-full text-xs text-[#1a6b3c] font-semibold hover:underline text-left"
            data-testid={`lease-view-btn-${lease.lease_id}`}
          >
            View lease details →
          </button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white rounded-2xl" data-testid={`lease-dialog-${lease.lease_id}`}>
          <DialogHeader>
            <DialogTitle className="font-['Fraunces'] text-2xl">
              Lease #{lease.lease_id.slice(-6).toUpperCase()}
            </DialogTitle>
            <DialogDescription className="text-[#5a5a5a]">
              Review the terms below, then sign to make this lease official.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <Term icon={<Calendar className="w-4 h-4" />} label="Start date" value={lease.start_date || "—"} />
              <Term icon={<Calendar className="w-4 h-4" />} label="End date" value={lease.end_date || "—"} />
              <Term icon={<DollarSign className="w-4 h-4" />} label="Monthly rent" value={`$${Number(lease.monthly_rent || 0).toLocaleString()}`} />
              <Term icon={<Shield className="w-4 h-4" />} label="Security deposit" value={`$${Number(lease.deposit || 0).toLocaleString()}`} />
            </div>

            <div className="bg-[#faf8f3] rounded-xl p-4 border border-[#d5d3ce]/60">
              <p className="text-xs font-semibold text-[#0d0d0d] mb-3">Signature status</p>
              <div className="space-y-2">
                <SigRow label="Renter" signed={lease.renter_signed} ts={lease.renter_signed_at} />
                <SigRow label="Landlord" signed={lease.landlord_signed} ts={lease.landlord_signed_at} />
              </div>
            </div>

            <p className="text-[11px] text-[#888] leading-relaxed">
              By clicking Sign, you agree to the terms above and electronically execute this lease in accordance with the E-SIGN Act (15 USC §7001). This digital signature has the same legal effect as a handwritten signature.
            </p>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="lease-sign-error">
                {error}
              </p>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-xl"
              data-testid="lease-dialog-close"
            >
              Close
            </Button>
            {!mySigned && lease.status !== "active" && (
              <Button
                data-testid="lease-dialog-sign-btn"
                onClick={handleSign}
                disabled={signing}
                className="rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white font-semibold"
              >
                {signing ? "Signing..." : <><CheckCircle2 className="w-4 h-4 mr-2" /> Sign Lease</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Term({ icon, label, value }) {
  return (
    <div className="bg-[#faf8f3] rounded-xl p-3 border border-[#d5d3ce]/60">
      <div className="flex items-center gap-1.5 text-[#888] text-[11px] mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <p className="text-sm font-semibold text-[#0d0d0d]">{value}</p>
    </div>
  );
}

function SigRow({ label, signed, ts }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <div className="flex items-center gap-2">
        {signed ? (
          <CheckCircle2 className="w-4 h-4 text-[#1a6b3c]" />
        ) : (
          <Clock className="w-4 h-4 text-amber-500" />
        )}
        <span className="font-medium text-[#0d0d0d]">{label}</span>
      </div>
      <span className="text-[#888]">
        {signed ? (ts ? new Date(ts).toLocaleDateString() : "Signed") : "Pending"}
      </span>
    </div>
  );
}
